import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { ArrowRight, CircleCheck, CircleHelp, House, Mail, MessageSquareText } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { CONTACT_MESSAGE_MAX_LENGTH, ContactFormData, contactSchema } from "@/model/schemas";
import { usePostContactUsMutation } from "@/store/service";
import { zodFormResolver } from "@/util/form";
import TransitionLink from "../TransitionLink";
import Container from "../site/Container";
import CtaBand from "../site/CtaBand";
import CtaLink, { ctaVariants } from "../site/CtaLink";
import { TextAreaField as BaseTextAreaField, TextField as BaseTextField } from "../site/FormFields";
import PageHeader from "../site/PageHeader";
import { GET_STARTED_PATH } from "../site/nav";

const TextField = BaseTextField<ContactFormData>;
const TextAreaField = BaseTextAreaField<ContactFormData>;

const CONTACT_EMAIL = "info@buysooner.com";

interface QuickLink {
  icon: LucideIcon;
  title: string;
  text: string;
  action: string;
  to: string;
}

const quickLinks: QuickLink[] = [
  {
    icon: House,
    title: "Ready to check your eligibility?",
    text: "Tell us about your situation and the home you want.",
    action: "Get started",
    to: GET_STARTED_PATH,
  },
  {
    icon: CircleHelp,
    title: "Looking for a quick answer?",
    text: "Common questions about BuySooner Boost, answered.",
    action: "Browse FAQs",
    to: "/faq",
  },
];

const defaultValues: ContactFormData = { name: "", email: "", phone: "", message: "" };

const cardClass = "rounded-[18px] border border-[#e1e9e9] bg-white p-6 shadow-[0_7px_24px_rgba(7,46,64,0.06)] sm:p-10";

const MessageSent = ({ name, email, onReset }: { name: string; email: string; onReset: () => void }) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => headingRef.current?.focus(), []);

  return (
    <div className={cardClass}>
      <span className="grid size-14 place-items-center rounded-full bg-mint">
        <CircleCheck className="size-7 text-brand-ink" strokeWidth={1.75} aria-hidden />
      </span>
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="mt-6 text-2xl font-bold tracking-tight text-navy outline-none sm:text-3xl"
      >
        Thanks, {name}. Your message is on its way.
      </h2>
      <p className="mt-3 text-lg leading-relaxed text-slate-600">
        We’ll get back to you at <span className="font-semibold break-all text-navy">{email}</span> as soon as we can.
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
        <CtaLink to="/">Back to home</CtaLink>
        <button
          type="button"
          onClick={onReset}
          className="cursor-pointer text-[15px] font-semibold text-brand-ink-strong transition-colors hover:text-navy"
        >
          Send another message
        </button>
      </div>
    </div>
  );
};

const ContactUs = () => {
  const [postContactUs, { isLoading, isSuccess, reset }] = usePostContactUsMutation();
  const form = useForm<ContactFormData>({
    resolver: zodFormResolver<ContactFormData>(contactSchema),
    mode: "onTouched",
    defaultValues,
  });

  const onSubmit = async (data: ContactFormData) => {
    toast.dismiss();
    const phone = data.phone.replace(/\s+/g, "");
    try {
      await postContactUs({
        name: data.name.trim(),
        email: data.email.trim(),
        phone: phone || undefined,
        message: data.message.trim(),
      }).unwrap();
    } catch (error) {
      const rateLimited = (error as { status?: number }).status === 429;
      toast.error(
        rateLimited
          ? "You’ve sent a few messages in a row. Please wait a minute and try again."
          : `We couldn’t send your message. Please try again, or email us at ${CONTACT_EMAIL}.`,
      );
    }
  };

  const sendAnother = () => {
    form.reset(defaultValues);
    reset();
  };

  const { name, email } = form.getValues();

  return (
    <>
      <section className="py-14 sm:py-20">
        <Container>
          <PageHeader eyebrow="Get in touch" title="We’re here to help.">
            <p>
              Have a question about BuySooner Boost, or about an enquiry you’ve already sent? Send us a message and the
              OnHome team will get back to you.
            </p>
          </PageHeader>

          <div className="mt-10 grid items-start gap-6 lg:grid-cols-[1.4fr_0.85fr]">
            {isSuccess ? (
              <MessageSent name={name.trim().split(/\s+/)[0]} email={email.trim()} onReset={sendAnother} />
            ) : (
              <FormProvider {...form}>
                <form noValidate onSubmit={form.handleSubmit(onSubmit)} className={cardClass}>
                  <h2 className="text-xl font-semibold text-navy">Send us a message</h2>
                  <div className="mt-7 grid gap-6 sm:grid-cols-2">
                    <TextField
                      name="name"
                      label="Your name"
                      autoComplete="name"
                      placeholder="e.g. Alex Smith"
                      className="sm:col-span-2"
                    />
                    <TextField
                      name="email"
                      label="Email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                    />
                    <TextField
                      name="phone"
                      label={
                        <>
                          Phone <span className="font-normal text-slate-500">(optional)</span>
                        </>
                      }
                      type="tel"
                      autoComplete="tel"
                      placeholder="04XX XXX XXX"
                    />
                    <TextAreaField
                      name="message"
                      label="How can we help?"
                      rows={6}
                      maxLength={CONTACT_MESSAGE_MAX_LENGTH}
                      placeholder="Tell us a little about your question or situation."
                      className="sm:col-span-2"
                    />
                  </div>
                  <div className="mt-8 flex flex-col-reverse gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[13px] leading-relaxed text-slate-500 sm:max-w-xs">
                      We only use your details to reply to your message. See our{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand-ink-strong underline-offset-2 hover:underline"
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>
                    <button type="submit" disabled={isLoading} className={ctaVariants({ className: "shrink-0" })}>
                      {isLoading ? "Sending…" : "Send message"}
                      <ArrowRight className="size-4" aria-hidden />
                    </button>
                  </div>
                </form>
              </FormProvider>
            )}

            <aside className="grid gap-6">
              <div className="rounded-[18px] bg-mint p-7 sm:p-9">
                <h2 className="text-lg font-semibold text-navy">Other ways to reach us</h2>
                <ul className="mt-6 space-y-8">
                  <li className="flex gap-4">
                    <Mail className="mt-0.5 size-7 shrink-0 text-brand-ink" strokeWidth={1.75} aria-hidden />
                    <div>
                      <h3 className="font-semibold text-navy">Email us</h3>
                      <a
                        href={`mailto:${CONTACT_EMAIL}`}
                        className="mt-1 inline-block text-[15px] font-medium text-brand-ink-strong underline-offset-2 hover:underline"
                      >
                        {CONTACT_EMAIL}
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <MessageSquareText
                      className="mt-0.5 size-7 shrink-0 text-brand-ink"
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <div>
                      <h3 className="font-semibold text-navy">We read every message</h3>
                      <p className="mt-1 text-[15px] leading-relaxed text-slate-600">
                        A member of the OnHome team will get back to you as soon as possible.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              <ul className="grid gap-4">
                {quickLinks.map(({ icon: Icon, title, text, action, to }) => (
                  <li key={to}>
                    <TransitionLink
                      to={to}
                      className="group flex gap-4 rounded-[18px] border border-[#e1e9e9] bg-white p-6 transition-shadow hover:shadow-[0_7px_24px_rgba(7,46,64,0.08)]"
                    >
                      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-mint">
                        <Icon className="size-5 text-brand-ink" strokeWidth={1.75} aria-hidden />
                      </span>
                      <span>
                        <span className="block font-semibold text-navy">{title}</span>
                        <span className="mt-1 block text-[15px] leading-relaxed text-slate-600">{text}</span>
                        <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink-strong">
                          {action}
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                        </span>
                      </span>
                    </TransitionLink>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </Container>
      </section>

      <CtaBand />
    </>
  );
};

export default ContactUs;
